<div class="monthly-toolbar">
  <label class="period-field">
    <span>Period</span>
    <select class="period-select" [(ngModel)]="period" (ngModelChange)="load()" [disabled]="!!busyArtistId">
      <option *ngFor="let p of periods" [value]="p">{{ p }}</option>
    </select>
  </label>
  <button type="button" class="secondary-btn touch-btn" (click)="load()" [disabled]="isLoading">Refresh</button>
  <button type="button" class="secondary-btn touch-btn" (click)="exportSummaryPdf()"
    [disabled]="isLoading || !result || isExportingSummary" [attr.aria-busy]="isExportingSummary">
    <span class="btn-spinner" *ngIf="isExportingSummary" aria-hidden="true"></span>
    {{ isExportingSummary ? 'Generating...' : 'Export PDF' }}
  </button>
</div>

<p class="info-note">Prints from removed machines and stores are included. Frames without an artist are company-owned and are not paid.</p>

<p class="status-line" role="status" *ngIf="isLoading">
  Reading machine usage… {{ progress.total ? progress.done + ' of ' + progress.total + ' machines' : '' }}
</p>
<p class="error-line" role="alert" *ngIf="loadError">{{ loadError }}</p>

<ng-container *ngIf="result && !isLoading">
  <div class="warn-banner" role="alert" *ngIf="result.missingData.length > 0">
    <strong>Missing data:</strong>
    {{ result.missingData.length }} machine{{ result.missingData.length === 1 ? '' : 's' }} recorded sessions in {{ period }} but no frame usage:
    <span class="missing-list">{{ missingDataNames.join(', ') }}</span>.
    Totals below are not adjusted. Emailing a statement asks you to confirm.
  </div>

  <div class="kpi-row">
    <div class="stat-card">
      <span class="stat-label">Total Frames</span>
      <span class="stat-value">{{ result.kpis.totalFrames }}</span>
    </div>
    <div class="stat-card">
      <span class="stat-label">Total Prints</span>
      <span class="stat-value">{{ result.kpis.totalPrints | number }}</span>
    </div>
    <div class="stat-card">
      <span class="stat-label">Total Amount</span>
      <span class="stat-value">{{ result.kpis.totalAmount | currency }}</span>
    </div>
  </div>

  <div class="card data-grid-scrollable">
    <table class="data-table">
      <thead>
        <tr>
          <th class="text-right">Rank</th>
          <th>Artist</th>
          <th class="text-right">Prints</th>
          <th class="text-right">Amount</th>
          <th>Status</th>
          <th class="text-center">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let a of result.artists; let i = index">
          <td class="text-right">{{ i + 1 }}</td>
          <td class="fw-500">
            {{ a.artistName }}
            <span class="status-badge status-dim" *ngIf="a.inactive">Inactive</span>
          </td>
          <td class="text-right">{{ a.totalPrints | number }}</td>
          <td class="text-right fw-600">
            <span *ngIf="!a.rateMissing">{{ a.totalAmount | currency }}</span>
            <span class="status-badge status-warn" *ngIf="a.rateMissing">Rate missing</span>
          </td>
          <td><span class="status-badge" [ngClass]="badgeOf(a).cls">{{ badgeOf(a).text }}</span></td>
          <td class="text-center row-actions">
            <!-- same actions and icons as the SMRS invoice grid -->
            <button type="button" class="action-btn view-btn" (click)="openStatement(a)" [title]="'View statement for ' + a.artistName"
              [attr.aria-label]="'View statement for ' + a.artistName">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><circle cx="10" cy="14" r="3"></circle><path d="m12.1 16.1 2.9 2.9"></path></svg>
            </button>
            <button type="button" class="action-btn email-btn" (click)="emailStatement(a)"
              [disabled]="!!emailDisabledReason(a) || !!busyArtistId"
              [title]="emailDisabledReason(a) || ('Email statement to ' + a.artistName)" [attr.aria-label]="'Email statement to ' + a.artistName"
              [attr.aria-busy]="busyArtistId === a.artistId">
              <span class="btn-spinner" *ngIf="busyArtistId === a.artistId; else mailIcon" aria-hidden="true"></span>
              <ng-template #mailIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
              </ng-template>
            </button>
          </td>
        </tr>
        <tr *ngIf="result.artists.length === 0">
          <td colspan="6" class="empty-table-msg">No artist-owned frames were printed in {{ period }}.</td>
        </tr>
      </tbody>
      <tfoot *ngIf="result.artists.length > 0">
        <tr>
          <th></th>
          <th>Total</th>
          <th class="text-right">{{ result.kpis.totalPrints | number }}</th>
          <th class="text-right">{{ result.kpis.totalAmount | currency }}</th>
          <th colspan="2"></th>
        </tr>
      </tfoot>
    </table>
  </div>

  <!-- Company-owned (unassigned) frames are not listed (user, 2026-10-27); only frames of a deleted artist are flagged -->
  <section class="card unassigned-panel" aria-labelledby="review-title" *ngIf="result.needsReview.length > 0">
    <h3 id="review-title" class="review-title">Needs review (owner deleted)</h3>
    <p class="info-note">These frames belong to a deleted artist and are not paid until they are reassigned in Artist Management.</p>
    <ul class="frame-list">
      <li *ngFor="let f of result.needsReview">
        <span class="frame-id">{{ f.frameId }}</span>
        <span class="frame-prints">{{ f.prints | number }} prints · was {{ f.ownerName }}</span>
      </li>
    </ul>
  </section>
</ng-container>

<!-- Statement popup (like the SMRS invoice view): the statement document with PDF and email actions -->
<div class="modal-overlay" *ngIf="selected" (click)="closeStatement()">
  <div class="statement-modal" role="dialog" aria-modal="true" aria-labelledby="statement-title" (click)="$event.stopPropagation()">
    <div class="statement-head">
      <h2 id="statement-title">Artist Statement · {{ selected.artistName }} · {{ period }}</h2>
      <div class="head-actions">
        <button type="button" class="action-btn view-btn touch-btn" (click)="savePdf()" [disabled]="isGeneratingPdf || !!busyArtistId || isStatementLoading(selected)">
          <span class="btn-spinner" *ngIf="isGeneratingPdf && !busyArtistId" aria-hidden="true"></span>
          {{ isGeneratingPdf && !busyArtistId ? 'Generating...' : 'Save to PDF' }}
        </button>
        <button type="button" class="action-btn view-btn touch-btn" (click)="printPdf()" [disabled]="isGeneratingPdf || !!busyArtistId || isStatementLoading(selected)">
          {{ isGeneratingPdf && !busyArtistId ? 'Generating...' : 'Open PDF' }}
        </button>
        <button type="button" class="action-btn email-btn touch-btn" (click)="emailStatement(selected)"
          [disabled]="!!emailDisabledReason(selected) || isGeneratingPdf || !!busyArtistId"
          [title]="emailDisabledReason(selected) || 'Email statement'" [attr.aria-busy]="busyArtistId === selected.artistId">
          <span class="btn-spinner" *ngIf="busyArtistId === selected.artistId" aria-hidden="true"></span>
          {{ busyArtistId === selected.artistId ? 'Preparing...' : 'Email' }}
        </button>
        <button type="button" class="close-btn-static touch-btn" (click)="closeStatement()" aria-label="Close">&times;</button>
      </div>
    </div>

    <div class="statement-body">
      <div class="warn-banner" role="alert" *ngIf="missingDataNames.length > 0">
        Usage data is incomplete for {{ period }} ({{ missingDataNames.join(', ') }}).
      </div>
      <p class="info-note" *ngIf="emailDisabledReason(selected) as reason">{{ reason }}</p>
      <div class="loading-block" role="status" *ngIf="isStatementLoading(selected); else statementDoc">
        <span class="btn-spinner large" aria-hidden="true"></span>
        <span>Loading statement…</span>
      </div>
      <ng-template #statementDoc>
        <app-artist-statement-template *ngIf="documentFor(selected) as d" [statement]="d" [rateMissing]="selected.rateMissing"></app-artist-statement-template>
      </ng-template>
    </div>
  </div>
</div>

<!-- Hidden container for PDF generation (same pattern as the SMRS invoice) -->
<div class="print-container" *ngIf="printTarget">
  <div id="artist-monthly-pdf-content" class="invoice-modal">
    <app-artist-statement-template *ngIf="documentFor(printTarget) as d" [statement]="d" [forPdf]="true"
      [rateMissing]="printTarget.rateMissing"></app-artist-statement-template>
  </div>
</div>

<!-- Hidden container for the summary PDF (KPIs + ranking, laid out like the Profit Report's Total P/L PDF) -->
<div class="print-container" *ngIf="isExportingSummary && result">
  <div id="artist-summary-pdf-content" class="summary-pdf">
    <div class="summary-pdf-card summary-pdf-header">
      <div>
        <h2>Artist Statement Summary</h2>
        <p>{{ companyName }}</p>
      </div>
      <div class="summary-pdf-dates">
        <div><label>Statement Period</label><span>{{ period }}</span></div>
        <div><label>Generated</label><span>{{ today | date:'mediumDate' }}</span></div>
      </div>
    </div>

    <div class="summary-pdf-kpis">
      <div class="summary-pdf-card summary-pdf-kpi">
        <label>Total Frames</label>
        <span class="value">{{ result.kpis.totalFrames | number }}</span>
      </div>
      <div class="summary-pdf-card summary-pdf-kpi">
        <label>Total Prints</label>
        <span class="value">{{ result.kpis.totalPrints | number }}</span>
      </div>
      <div class="summary-pdf-card summary-pdf-kpi highlight">
        <label>Total Amount</label>
        <span class="value">{{ result.kpis.totalAmount | currency }}</span>
      </div>
    </div>

    <div class="summary-pdf-card">
      <h3>Artist Ranking for {{ period }}</h3>
      <table class="summary-pdf-table">
        <thead>
          <tr>
            <th class="text-right">Rank</th>
            <th class="text-left">Artist</th>
            <th class="text-right">Prints</th>
            <th class="text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let a of result.artists; let i = index" class="avoid-page-break">
            <td class="text-right">{{ i + 1 }}</td>
            <td>{{ a.artistName }}<span class="summary-pdf-muted" *ngIf="a.inactive"> (Inactive)</span></td>
            <td class="text-right">{{ a.totalPrints | number }}</td>
            <td class="text-right">{{ a.rateMissing ? 'Rate missing' : (a.totalAmount | currency) }}</td>
          </tr>
          <tr *ngIf="result.artists.length === 0">
            <td colspan="4" class="text-center">No artist-owned frames were printed in {{ period }}.</td>
          </tr>
        </tbody>
        <tfoot *ngIf="result.artists.length > 0">
          <tr>
            <th></th>
            <th class="text-left">Total</th>
            <th class="text-right">{{ result.kpis.totalPrints | number }}</th>
            <th class="text-right">{{ result.kpis.totalAmount | currency }}</th>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</div>

<app-email-form *ngIf="isEmailOpen && emailData" [to]="emailData.to" [subject]="emailData.subject" [body]="emailData.body"
  [attachments]="emailData.attachments" [fixedAttachments]="true" templateType="artist-statement"
  (emailSent)="onEmailSent($event)" (close)="closeEmail()"></app-email-form>

